import { useEffect, type FC } from 'react'

import { config } from '@skytech/config'
import { LoaderCircle } from '@skytech/ui'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import DataPoints, { DataPoint } from '@/components/DataPoints'
import { LoadingError } from '@/components/LoadingError'
import { LocalDate } from '@/components/LocalDate'
import { LocalDateTime } from '@/components/LocalDateTime'
import RouterLink from '@/components/RouterLink'
import queries from '@/modules/admin/queries'
import { leasingResources } from '@/resources/leasing'
import { APP_ROUTES } from '@/util/app-routes'

// TODO: enable for production
const isDevEnv = config.ENVIRONMENT === 'dev'

export const EndOfLease: FC = () => {
  const params = useParams()
  const assetId = Number(params.id)
  const translate = useTranslation()
  const navigate = useNavigate()

  if (isNaN(assetId)) throw Error(translate('Asset ID is not a number!'))

  const { data, isLoading, isError } = useQuery(
    queries.adminLeasingAssetDetails(assetId),
    () => leasingResources.getLeasingAssetDetails(assetId),
    { enabled: isDevEnv }
  )
  if (isDevEnv && isLoading) {
    return <LoaderCircle />
  }
  if (isDevEnv && isError) {
    return <LoadingError />
  }

  const currentEndOfLeaseWorkflow = isDevEnv ? data?.currentEndOfLeaseWorkflow : undefined

  useEffect(() => {
    if (!isDevEnv || !data || !currentEndOfLeaseWorkflow) {
      navigate(APP_ROUTES.HARDWARE_ASSET.replace(':id', String(assetId)))
    }
  }, [data, currentEndOfLeaseWorkflow, navigate, assetId])

  const isAnyDate =
    currentEndOfLeaseWorkflow?.processStartDate || currentEndOfLeaseWorkflow?.endOfLeaseDate
  const isAnyEmployeeSelection =
    currentEndOfLeaseWorkflow?.employeeDecisionOpenDate ||
    currentEndOfLeaseWorkflow?.employeeDecisionDeadlineDate
  const isAnyProcess =
    currentEndOfLeaseWorkflow?.isActive ||
    currentEndOfLeaseWorkflow?.processStartedDatetime ||
    currentEndOfLeaseWorkflow?.workflowStep

  return (
    <div className="-mx-2 -my-4 flex flex-wrap">
      <section className="shadow-basic m-2 flex flex-1 basis-[30rem] items-start rounded-md bg-white p-4">
        <DataPoints>
          {isAnyDate && (
            <div className="flex basis-[30rem]">
              {currentEndOfLeaseWorkflow?.processStartDate && (
                <DataPoint
                  header={<Translation id="End of lease start date" />}
                  value={<LocalDateTime date={currentEndOfLeaseWorkflow.processStartDate} />}
                />
              )}
              {currentEndOfLeaseWorkflow?.endOfLeaseDate && (
                <DataPoint
                  header={<Translation id="End of lease date" />}
                  value={<LocalDateTime date={currentEndOfLeaseWorkflow.endOfLeaseDate} />}
                />
              )}
            </div>
          )}

          {isAnyEmployeeSelection && (
            <>
              <h3>
                <Translation id="Employee selection" />
              </h3>
              <div className="flex basis-[30rem]">
                {currentEndOfLeaseWorkflow?.employeeDecisionOpenDate && (
                  <DataPoint
                    header={<Translation id="Open date" />}
                    value={<LocalDate date={currentEndOfLeaseWorkflow.employeeDecisionOpenDate} />}
                  />
                )}
                {currentEndOfLeaseWorkflow?.employeeDecisionDeadlineDate && (
                  <DataPoint
                    header={<Translation id="Deadline date" />}
                    value={
                      <LocalDate date={currentEndOfLeaseWorkflow.employeeDecisionDeadlineDate} />
                    }
                  />
                )}
              </div>
            </>
          )}
        </DataPoints>
      </section>
      <section className="shadow-basic m-2 flex flex-1 basis-[30rem] rounded-md bg-white p-4">
        <DataPoints>
          {isAnyProcess && (
            <>
              {' '}
              <h3>
                <Translation id="Process" />
              </h3>
              <div className="w-full flex-col flex-wrap">
                {currentEndOfLeaseWorkflow?.isActive && (
                  <DataPoint
                    header={<Translation id="Active" />}
                    value={currentEndOfLeaseWorkflow.isActive ? 'Is active' : 'Not active'}
                  />
                )}
                {currentEndOfLeaseWorkflow?.processStartedDatetime && (
                  <DataPoint
                    header={<Translation id="Process started date time" />}
                    value={
                      <LocalDateTime date={currentEndOfLeaseWorkflow.processStartedDatetime} />
                    }
                  />
                )}
                {currentEndOfLeaseWorkflow?.workflowStep && (
                  <DataPoint
                    header={<Translation id="Workflow step" />}
                    value={<Translation id={currentEndOfLeaseWorkflow.workflowStep} />}
                  />
                )}
              </div>
            </>
          )}

          {(currentEndOfLeaseWorkflow?.completedDatetime || data?.orderId) && (
            <div className="flex basis-[30rem]">
              {currentEndOfLeaseWorkflow?.completedDatetime && (
                <DataPoint
                  header={<Translation id="Completed date time" />}
                  value={<LocalDateTime date={currentEndOfLeaseWorkflow.completedDatetime} />}
                />
              )}
              {data?.orderId && (
                <DataPoint
                  header={<Translation id="Order Id" />}
                  value={
                    <RouterLink
                      to={APP_ROUTES.HARDWARE_ECOM_POLICY_ORDER.replace(
                        ':ecomPolicyOrderId',
                        String(data.orderId)
                      )}
                      hasUnderline={false}
                    >
                      <div className="text-lg">{data.orderId}</div>
                    </RouterLink>
                  }
                />
              )}
            </div>
          )}
          {currentEndOfLeaseWorkflow?.cancelledDatetime && (
            <div className="w-full">
              <DataPoint
                header={<Translation id="Cancelled date time" />}
                value={<LocalDateTime date={currentEndOfLeaseWorkflow.cancelledDatetime} />}
              />
            </div>
          )}
        </DataPoints>
      </section>
    </div>
  )
}
